import { useEffect, useRef, useState } from "react";
import api, { formatError } from "../lib/api";
import { Send, Sparkles, Loader2, Bot, User } from "lucide-react";

const TIME = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

const QUICK_PROMPTS = [
  "Reformule la question 2, le professeur la trouve ambiguë.",
  "Rends les propositions de la question 3 plus proches entre elles.",
  "Augmente la difficulté générale d'un cran.",
  "Remplace la question 4 par un nouveau problème de calcul.",
];

export default function ChatPanel({ rattrapageId, onUpdated }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const scrollRef = useRef(null);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/rattrapages/${rattrapageId}/chat`);
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rattrapageId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async (textParam) => {
    const text = (textParam ?? input).trim();
    if (!text || sending) return;
    setSending(true);
    setErr("");
    // optimistic user message
    const optimistic = { role: "user", content: text, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    setInput("");
    try {
      const { data } = await api.post(`/rattrapages/${rattrapageId}/chat`, { message: text });
      setMessages((m) => {
        // replace last optimistic with server-confirmed pair (user + assistant)
        const next = [...m];
        next.pop();
        next.push(data.user_message, data.reply);
        return next;
      });
      if (data.rattrapage) onUpdated?.(data.rattrapage);
    } catch (e) {
      setErr(formatError(e));
      // rollback optimistic
      setMessages((m) => m.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md flex flex-col" data-testid="chat-panel" style={{ minHeight: 420 }}>
      <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
        <Sparkles size={14} className="text-[#2563eb]" />
        <div className="label-xs">Chat avec l'IA · affiner le QCM</div>
        <div className="ml-auto text-xs text-slate-500">{messages.length} message{messages.length > 1 ? "s" : ""}</div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="chat-messages">
        {loading ? (
          <div className="text-sm text-slate-500">Chargement de la conversation…</div>
        ) : messages.length === 0 ? (
          <div className="text-center py-6">
            <Bot size={28} className="mx-auto text-slate-300 mb-3" />
            <div className="text-sm font-semibold text-slate-900">Demandez à l'IA d'affiner le QCM</div>
            <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
              Intégrez les retours du professeur, reformulez une question, ajustez la difficulté.
              Le QCM se met à jour automatiquement.
            </p>
            <div className="mt-5 flex flex-col items-center gap-1.5">
              {QUICK_PROMPTS.map((p, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => send(p)}
                  data-testid={`quick-prompt-${i}`}
                  className="text-xs px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-sm border border-slate-200"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m, i) => (
            <MessageBubble key={i} role={m.role} content={m.content} time={m.created_at} updatedQcm={m.updated_qcm} index={i} />
          ))
        )}
        {sending && (
          <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="chat-thinking">
            <Loader2 size={14} className="animate-spin text-[#2563eb]" /> L'IA réfléchit…
          </div>
        )}
      </div>

      {err && (
        <div className="px-5 py-2 text-xs text-[#b91c1c] bg-[#fef2f2] border-t border-red-100" data-testid="chat-error">{err}</div>
      )}

      <div className="border-t border-slate-200 p-3">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            rows={2}
            placeholder="Ex. « Le prof veut une question plus orientée pratique sur le chapitre 2 »"
            data-testid="chat-input"
            className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-sm focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 resize-none"
          />
          <button
            type="button"
            onClick={() => send()}
            disabled={sending || !input.trim()}
            data-testid="chat-send-btn"
            className="inline-flex items-center gap-1 bg-[#2563eb] text-white px-3 py-2 text-sm font-semibold rounded-sm hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-400"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </button>
        </div>
        <div className="text-[11px] text-slate-500 mt-1.5">
          Entrée pour envoyer · Maj+Entrée pour un saut de ligne. Le QCM est sauvegardé automatiquement après chaque retour.
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ role, content, time, updatedQcm, index }) {
  const isUser = role === "user";
  return (
    <div className={`flex gap-2 ${isUser ? "justify-end" : "justify-start"}`} data-testid={`msg-${index}`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-sm bg-slate-900 text-white flex items-center justify-center shrink-0">
          <Bot size={14} />
        </div>
      )}
      <div className={`max-w-[78%] ${isUser ? "items-end" : "items-start"} flex flex-col`}>
        <div
          className={`px-3 py-2 rounded-sm text-sm whitespace-pre-wrap leading-relaxed ${
            isUser ? "bg-[#0f172a] text-white" : "bg-slate-100 text-slate-900 border border-slate-200"
          }`}
        >
          {content}
        </div>
        <div className="flex items-center gap-2 text-[10px] text-slate-500 mt-1 uppercase tracking-wider">
          <span>{TIME(time)}</span>
          {!isUser && updatedQcm && (
            <span className="text-[#15803d] font-semibold">QCM mis à jour</span>
          )}
        </div>
      </div>
      {isUser && (
        <div className="w-7 h-7 rounded-sm bg-slate-200 text-slate-700 flex items-center justify-center shrink-0">
          <User size={14} />
        </div>
      )}
    </div>
  );
}
